"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import SubCount from "@/components/global/youtube/SubCount";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import youtube from "@/public/animatedAssets/youtube.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset5 = ({
  className = "",
  language,
}: {
  className?: string;
  language: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const shine = {
    collapsed: {
      backgroundPosition: ["-60% 0%", "160% 0%"],
      transition: {
        backgroundPosition: {...ease, duration: 1.2}
      }
    },
    expanded: {
      backgroundPosition: ["160% 0%", "-60% 0%"],
      transition: {
        backgroundPosition: {...ease, duration: 1.2}
      }
    },
  };

  const pillTilt = {
    collapsed: {rotate: 0, y: "1rem", transition: ease},
    expanded: {rotate: -6, y: "1rem", transition: ease},
  };

  const youtubeCenter = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "1rem", transition: ease},
  };

  const youtubeBottom = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "2rem", transition: ease},
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center px-[2rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="w-[min(51rem,calc(100%-4rem)] relative mb-[.5rem] text-[4.5rem] leading-[1.1em] font-medium text-center font-funnel text-white/40"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) 35%, #D9F9B9 45%, #FFFFFF 50%, #D9F9B9 55%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.4) 100%)",
          backgroundSize: "300% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",

        }}
        variants={shine}
        animate={controls}
        initial="collapsed"
      >
        <div style={{
          WebkitTextFillColor: "rgba(255,255,255,0.4)",
          color: "rgba(255,255,255,0.4)"
        }}>
          <SubCount
            handle="@Bitbulldaily"
            language={language}
          />
        </div>
      </motion.div>

      <div className="flex items-end gap-[2rem]">
        <motion.div
          className="w-[7rem] h-[7rem] translate-y-[1rem] relative z-10 bg-[#D9F9B9]/[.25] rounded-[1.5rem] inline-flex justify-center items-center"
          variants={pillTilt}
          animate={controls}
          initial="collapsed"
        >
          <Image
            className="!w-[5rem]"
            src={youtube}
            alt="YouTube logo"
          />
          <div className="top-0 right-0 -translate-y-[1rem] translate-x-[2rem] absolute w-[3.375rem] h-[3.375rem] bg-[#D9F9B9] rounded-full flex items-center justify-center text-[#222222] text-[2.25rem] leading-none">
            2
          </div>
        </motion.div>

        <p className="mb-0 inline-flex items-center relative bg-[#0A1A1D] text-[7.5rem] italic font-ivy font-normal leading-[.9] pr-[1rem]">
          <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#F7FFEF] to-[#D9F9B9]">
            Youtube
          </span>

          <motion.span
            className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-b from-[#F7FFEF] to-[#D9F9B9] opacity-60"
            variants={youtubeCenter}
            animate={controls}
            initial="collapsed"
          >
            Youtube
          </motion.span>

          <motion.span
            className="absolute inset-0 text-white/40"
            style={{WebkitTextFillColor: "rgba(255,255,255,0.4)", color: "rgba(255,255,255,0.4)"}}
            variants={youtubeBottom}
            animate={controls}
            initial="collapsed"
          >
            Youtube
          </motion.span>
        </p>
      </div>
    </div>
  );
};
